/**
 * WorkflowStatusPanel — Shows the live workflow instance for an entity.
 *
 * Displays: overall status badge, current level, pending approver,
 *           submitted by / date, last action.
 *
 * Props:
 *   entityName — e.g. "GlJournal"
 *   entityId   — UUID of the entity
 */

import { useEffect, useState } from 'react';
import { CheckCircle2, Clock, XCircle, SkipForward, AlertCircle, GitBranch } from 'lucide-react';
import { workflowApi } from '@/api/workflow.api';
import type { WorkflowInstanceDto, WorkflowApprovalDto } from '@/types/workflow.types';
import { OVERALL_STATUS_LABELS, OVERALL_STATUS_COLORS, APPROVAL_STATUS_LABELS, APPROVAL_STATUS_COLORS } from '@/types/workflow.types';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui/Spinner';

interface WorkflowStatusPanelProps {
  entityName: string;
  entityId:   string;
  className?: string;
}

const ApprovalStatusIcon = ({ status }: { status: WorkflowApprovalDto['status'] }) => {
  switch (status) {
    case 'Approved':  return <CheckCircle2 size={14} className="text-green-500" />;
    case 'Rejected':  return <XCircle size={14} className="text-red-500" />;
    case 'Pending':   return <Clock size={14} className="text-amber-500" />;
    case 'Delegated': return <SkipForward size={14} className="text-blue-500" />;
    case 'Escalated': return <AlertCircle size={14} className="text-purple-500" />;
    default:          return <Clock size={14} className="text-slate-400" />;
  }
};

export const WorkflowStatusPanel = ({ entityName, entityId, className }: WorkflowStatusPanelProps) => {
  const [instance, setInstance] = useState<WorkflowInstanceDto | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    workflowApi.getInstance(entityName, entityId)
      .then(r => setInstance(r.data.data ?? null))
      .catch(() => setError('No active workflow for this document.'))
      .finally(() => setLoading(false));
  }, [entityName, entityId]);

  if (loading) {
    return (
      <div className={cn('flex items-center justify-center py-8', className)}>
        <Spinner size="sm" />
      </div>
    );
  }

  if (error || !instance) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-10 text-slate-400', className)}>
        <GitBranch size={28} className="mb-2 opacity-30" />
        <p className="text-sm">{error ?? 'No workflow has been started for this document.'}</p>
      </div>
    );
  }

  const overallColor = OVERALL_STATUS_COLORS[instance.overallStatus];
  const overallLabel = OVERALL_STATUS_LABELS[instance.overallStatus];
  const pending = instance.approvals.find(a => a.status === 'Pending');

  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Workflow</p>
          <p className="text-sm font-semibold text-slate-800 dark:text-white">{instance.definitionName}</p>
        </div>
        <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', overallColor)}>
          {overallLabel}
        </span>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs text-slate-500 dark:text-gray-400 mb-1">
          <span>Level {instance.currentLevel} of {instance.totalLevels}</span>
          <span>{Math.round((instance.currentLevel / instance.totalLevels) * 100)}%</span>
        </div>
        <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-1.5">
          <div
            className="bg-blue-500 h-1.5 rounded-full transition-all"
            style={{ width: `${(instance.currentLevel / instance.totalLevels) * 100}%` }}
          />
        </div>
      </div>

      {/* Pending approver */}
      {pending && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800
                        rounded-lg px-4 py-3">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">
            Awaiting Approval — Level {pending.levelNumber}: {pending.levelName}
          </p>
          {pending.assignedUserName && (
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Assigned to: <strong>{pending.assignedUserName}</strong>
            </p>
          )}
        </div>
      )}

      {/* Approval steps */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          Approval Levels
        </p>
        {instance.approvals.map(a => (
          <div
            key={a.id}
            className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800
                       border border-slate-100 dark:border-slate-700"
          >
            <div className="mt-0.5">
              <ApprovalStatusIcon status={a.status} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {a.levelName}
                </p>
                <span className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-semibold',
                  APPROVAL_STATUS_COLORS[a.status],
                )}>
                  {APPROVAL_STATUS_LABELS[a.status]}
                </span>
              </div>
              {a.assignedUserName && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {a.status === 'Pending' ? 'Pending: ' : 'By: '}
                  {a.assignedUserName}
                </p>
              )}
              {a.actionDate && (
                <p className="text-xs text-slate-400 mt-0.5">
                  {new Date(a.actionDate).toLocaleString()}
                </p>
              )}
              {a.remarks && (
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 italic">
                  "{a.remarks}"
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
